import "./index.css";

import React from 'react';
import DialogDataComponent, {executeComponentCallback} from 'Core/components/DialogDataComponent';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import * as actions from './actions';
import FormWrapper, {FormField} from 'Core/components/advanced/FormWrapper';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import {getPageActions} from 'Core/helpers/redux';
import PhoneInput from 'Core/components/input/PhoneInput';
import SelectInput from 'Core/components/input/SelectInput';
import PhoneNumberOptionComponent from '../../selectInputOptions/PhoneNumberOption';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from 'Core/components/display/Button';
import ConfirmDialog from 'Core/components/dialogs/ConfirmDialog';
import {getArray, getString, trimArray} from 'Core/helpers/data';
import DataValueValidation from 'Core/validation';

const allActions = getPageActions(actions);

class EFaxDialog extends DialogDataComponent {
	phoneInputRef = React.createRef();
	phoneSelectRef = React.createRef();
	
	constructor(props) {
		super(props, {
			data: {
				eFaxNumber: '',
			},

			/**
			 * List of already saved eFax numbers loaded from IO.
			 * @type {string[]|null}
			 */
			loadedNumbers: null,
		}, {
			translationPath: 'EFaxDialog',
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onClose', 'onChange', 'onSave', 'onDelete', ...Object.keys(allActions)],
			
			// Custom component options
			phoneNumberPrefix: '+',
		});
		
		// Phone select methods
		this.loadPhonebook = this.loadPhonebook.bind(this);
		this.handlePhoneSelectOptionDelete = this.handlePhoneSelectOptionDelete.bind(this);
	}

	async asyncComponentDidMount(override = false) {
		await super.asyncComponentDidMount(override);
		
		this.loadPhonebook(true);
	}


	// Phone select methods ---------------------------------------------------------------------------------------------
	/**
	 * Load the eFax phonebook into the local state.
	 * @param {boolean} onMount - Flag indicating whether the action is being executed during the mount cycle.
	 */
	loadPhonebook(onMount = false) {
		const {study, fetchEFaxNumbersAction} = this.props;
		const executeAbortableAction = (!!onMount ? this.executeAbortableActionMount : this.executeAbortableAction);

		const loading = this.showLoading('#e-fax-number-input-wrapper');
		executeAbortableAction(fetchEFaxNumbersAction, getString(study, 'id'))
			.then(loadedNumbers => {
				if (getArray(loadedNumbers).length === 1) this.setValue('eFaxNumber', loadedNumbers[0]).then();
				return this.setState({loadedNumbers});
			})
			.finally(() => this.hideLoading(loading));
	}
	
	/**
	 * Handle phone select option delete button click.
	 * 
	 * @param {object} props
	 * @param {string} props.label
	 * @param {string} props.value
	 */
	handlePhoneSelectOptionDelete({label, value}) {
		const {study, openDialogAction, closeDialogAction, deleteEFaxNumberAction} = this.props;
		
		if (!value) return;
		
		const dialogGUIID = openDialogAction('', ConfirmDialog, {
			message: this.t('confirmNumberDelete', '', '', {number: label}),
			supportHtml: true,
			onYes: () => {
				this.executeAbortableAction(deleteEFaxNumberAction, getString(study, 'id'), value)
					.then(res => {
						if (!!res) this.loadPhonebook();
						closeDialogAction(dialogGUIID);
					});
			},
		}, {
			id: 'e-fax-confirm-phone-delete-dialog',
			closeOnEscape: true,
			closeOnClickOutside: true,
			hideCloseBtn: true,
			maxWidth: 600,
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}


	// Validation and error methods -------------------------------------------------------------------------------------
	/**
	 * Default component's data validation method
	 *
	 * @return {boolean} True if the component's data validation passed successfully.
	 */
	validate() {
		const dataValidation = new DataValueValidation();
		const dataToValidate = this.getData();

		dataValidation.addRule('eFaxNumber', 'required');

		const validationErrors = dataValidation.run(dataToValidate);
		if (validationErrors) this.setValidationErrors('', validationErrors).then();
		else this.clearValidationErrors().then();
		return !validationErrors;
	}


	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Dialog save method used to send the eFax message.
	 */
	save() {
		const {report, study, sendEFaxReportAction, dialogGUIID, addSuccessMessageAction} = this.props;
		const data = this.getDataToReturn();

		// Do the validation
		const isValid = this.validate();

		// If validation is successful
		if (isValid) {
			// Send the eFax to a specified number
			this.executeAbortableAction(sendEFaxReportAction, getString(study,'id'), getString(data,'eFaxNumber'), report)
				.then(res => {
					// Trigger component's onSave event with component's return data as param
					if (!!res) {
						addSuccessMessageAction(
							'', undefined, undefined, undefined, undefined, undefined, true, {
								closeOnEscape: true,
								closeOnClickOutside: false,
								hideCloseBtn: true,
								maxWidth: 480,
							}, {
								title: this.t('sendSuccessMessage'),
							}
						);
						executeComponentCallback(this.props.onSave, data, dialogGUIID);
						this.close();
					} else {
						if (!!this.phoneInputRef?.current?.phoneInputRef?.numberInputRef) {
							this.phoneInputRef.current.phoneInputRef.numberInputRef.focus();
						}
					}
				});
		}
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {loadedNumbers} = this.state;
		
		return this.renderDialog(
			this.renderTitle(this.t('title')),
			(
				<FormWrapper id="e-fax-form-wrapper">
					<br />
					<FormField 
						label={this.t('numberField')} 
						labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
						errorMessages={this.getValidationErrors('eFaxNumber')}
					>
						<div id="e-fax-number-input-wrapper">
							<PhoneInput
								prefix={this.getOption('phoneNumberPrefix')}
								disableDropdown={true}
								jumpCursorToEnd={true}
								cleanInput={false}
								value={this.getValue('eFaxNumber')}
								onChange={v => this.handleValueChange(
									'eFaxNumber', `${this.getOption('phoneNumberPrefix')}${v}`
								)}
								onEnterKey={() => {
									const sendBtn = document.querySelector(
										'#e-fax-dialog .dialog-content-component > .buttons > .button-component.success'
									);
									if (!!sendBtn) sendBtn.focus();
								}}
								ref={this.phoneInputRef}
							/>
							<Button
								title={this.t('numberSelectButton')}
								icon="address-book"
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={() => {
									if (!!this.phoneSelectRef?.current?.element) {
										this.phoneSelectRef.current.element.focus();
									}
								}}
							/>
						</div>
						<SelectInput
							id="e-fax-number-select"
							openMenuOnFocus={true}
							blurInputOnSelect={true}
							options={getArray(loadedNumbers).map(n => ({label: n, value: n}))}
							isLoading={(loadedNumbers === null)}
							value={this.getValue('eFaxNumber')}
							components={{Option: PhoneNumberOptionComponent}}
							labelProps={{onDelete: this.handlePhoneSelectOptionDelete}}
							onChange={v => this.handleValueChange('eFaxNumber', v)}
							onMenuClose={() => {
								if (!!this.phoneInputRef?.current?.phoneInputRef?.numberInputRef) {
									this.phoneInputRef.current.phoneInputRef.numberInputRef.focus();
								}
							}}
							placeholder={this.t('numberSelectPlaceholder')}
							ref={this.phoneSelectRef}
						/>
					</FormField>
				</FormWrapper>
			), {
				saveLabel: this.t('sendLabel'), 
				saveIcon: 'paper-plane-o',
			}
		)
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
EFaxDialog.propTypes = {
	report: PropTypes.object.isRequired,
	study: PropTypes.object.isRequired,

	// Events
	onSave: PropTypes.func,
};

export default connect(null, allActions, null, {forwardRef: true})(EFaxDialog);